import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";
import { useLang } from "../i18n/LanguageContext";

export default function Header() {
  const { lang, t, switchLang } = useLang();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const base = `/${lang}`;
  const links = [
    ["", t.nav.home],
    ["/services", t.nav.services],
    ["/about", t.nav.about],
    ["/contact", t.nav.contact],
  ];

  return (
    <header data-testid="site-header" className="sticky top-0 z-50 border-b border-navy-900/10 bg-paper-warm/85 backdrop-blur-xl">
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 sm:px-8">
        <Link to={base} data-testid="header-logo-link" aria-label="Tonouh Marketing — home" className="shrink-0">
          <Logo />
        </Link>

        <nav data-testid="header-nav" className="hidden items-center gap-8 lg:flex" aria-label="Main navigation">
          {links.map(([to, label]) => (
            <NavLink
              key={to}
              to={`${base}${to}`}
              end={to === ""}
              className={({ isActive }) =>
                `font-heading text-sm font-semibold tracking-wide transition-colors duration-200 ${
                  isActive ? "text-teal-700" : "text-navy-900/65 hover:text-navy-900"
                }`
              }
            >
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3 sm:gap-4">
          <div data-testid="lang-switch" className="flex items-center rounded-full border border-navy-900/15 p-1" role="group" aria-label="Language / Taal">
            {["en", "nl"].map((l) => (
              <button
                key={l}
                data-testid={`lang-switch-${l}`}
                onClick={() => switchLang(l)}
                aria-pressed={lang === l}
                className={`rounded-full px-3 py-1 font-heading text-xs font-bold uppercase tracking-widest transition-colors duration-200 ${
                  lang === l ? "bg-navy-900 text-paper-warm" : "text-navy-900/55 hover:text-navy-900"
                }`}
              >
                {l}
              </button>
            ))}
          </div>

          <Link to={`${base}/contact`} data-testid="header-contact-button" className="btn-primary hidden !px-5 !py-2.5 md:inline-flex">
            {t.nav.cta}
          </Link>

          <button
            data-testid="mobile-menu-button"
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-navy-900/15 text-navy-900 transition-colors duration-200 hover:bg-paper-sand lg:hidden"
            aria-expanded={open}
            aria-label={open ? t.nav.close : t.nav.menu}
            onClick={() => setOpen(!open)}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-navy-900/10 bg-paper-warm lg:hidden"
            aria-label="Mobile navigation"
          >
            <div className="flex flex-col px-6 pb-8 pt-2">
              {links.map(([to, label]) => (
                <NavLink
                  key={to}
                  to={`${base}${to}`}
                  end={to === ""}
                  className={({ isActive }) =>
                    `border-b border-navy-900/10 py-4 font-display text-2xl transition-colors duration-200 ${
                      isActive ? "text-teal-700" : "text-navy-900"
                    }`
                  }
                >
                  {label}
                </NavLink>
              ))}
              <Link to={`${base}/contact`} data-testid="mobile-contact-button" className="btn-primary mt-6">
                {t.nav.cta}
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
